import { useState, useRef } from 'react';
import type { DebugLog, DebugLogEntry } from '@n8n-builder/api-client';
import { createApiClient } from '@n8n-builder/api-client';
import { useChatStore } from '../../store/chat';
import { API_BASE_URL } from '../../lib/api-config';
import { FlagIcon, SpinnerIcon, CheckIcon } from '../ui/icons';

interface ReportIssuePanelProps {
  logs: DebugLog[];
}

// ── Compact log helpers (private) ────────────────────────────────────

function truncate(str: unknown, maxLen: number): string {
  const s = typeof str === 'string' ? str : JSON.stringify(str) ?? '';
  return s.length <= maxLen ? s : s.slice(0, maxLen) + '…';
}

function summarizeConversationContext(data: Record<string, unknown>): Record<string, unknown> {
  const messages = data.messages as Array<Record<string, unknown>> | undefined;
  const workflow = data.existingWorkflow as Record<string, unknown> | undefined;
  const msgSummary = messages
    ? { count: messages.length, roles: messages.map((m) => m.role), lastMessagePreview: truncate(messages[messages.length - 1]?.content, 200) }
    : undefined;
  const nodes = (workflow?.nodes as Array<Record<string, unknown>>) ?? [];
  const connections = workflow?.connections as Record<string, unknown> | undefined;
  const wfSummary = workflow
    ? { name: workflow.name, nodeCount: nodes.length, nodeTypes: nodes.map((n) => n.type), connectionCount: connections ? Object.keys(connections).length : 0 }
    : undefined;
  return { ...Object.fromEntries(Object.entries(data).filter(([k]) => k !== 'messages' && k !== 'existingWorkflow')), messages: msgSummary, existingWorkflow: wfSummary };
}

function summarizeApiRequest(data: Record<string, unknown>): Record<string, unknown> {
  const messages = data.messages as Array<Record<string, unknown>> | undefined;
  const tools = data.tools as Array<Record<string, unknown>> | undefined;
  const msgSummary = messages
    ? { count: messages.length, roles: messages.map((m) => m.role), totalChars: messages.reduce((sum, m) => sum + (JSON.stringify(m.content) ?? '').length, 0), lastMessagePreview: truncate(messages[messages.length - 1]?.content, 200) }
    : undefined;
  return { model: data.model, systemPrompt: '[STRIPPED]', messages: msgSummary, tools: tools ? tools.map((t) => t?.name || t) : data.tools, ...(data.maxTokens != null ? { maxTokens: data.maxTokens } : {}) };
}

function summarizeApiResponse(data: Record<string, unknown>): Record<string, unknown> {
  const content = data.content as Array<Record<string, unknown>> | undefined;
  const contentSummary = content
    ? content.map((block) => {
        if (block.type === 'text') return { type: 'text', preview: truncate(block.text, 200), length: (typeof block.text === 'string' ? block.text : '').length };
        if (block.type === 'tool_use') return { type: 'tool_use', name: block.name, id: block.id };
        return { type: block.type };
      })
    : undefined;
  return { stopReason: data.stopReason, usage: data.usage, content: contentSummary };
}

function summarizeToolResult(data: Record<string, unknown>): Record<string, unknown> {
  const name = data.name as string | undefined;
  const result = data.result as Record<string, unknown> | undefined;
  if (name === 'get_node_schema' && result) return { ...data, result: { nodeType: result.type || result.nodeType, displayName: result.displayName, propertyCount: Array.isArray(result.properties) ? result.properties.length : undefined } };
  if (name === 'search_nodes' && result) {
    const results = (result.results || result.nodes || result) as Array<Record<string, unknown>>;
    if (Array.isArray(results)) return { ...data, result: { resultCount: results.length, top3: results.slice(0, 3).map((r) => r.displayName || r.name || r.type) } };
  }
  const resultStr = JSON.stringify(data.result ?? data.output);
  if (resultStr && resultStr.length > 500) return { ...data, result: truncate(resultStr, 500), output: undefined };
  return data;
}

function summarizeWorkflowGenerated(data: Record<string, unknown>): Record<string, unknown> {
  const workflow = (data.workflow || data) as Record<string, unknown>;
  const nodes = (workflow.nodes as Array<Record<string, unknown>>) ?? [];
  const connections = workflow.connections as Record<string, unknown> | undefined;
  return { workflowId: data.workflowId || workflow.id, name: workflow.name, nodeCount: nodes.length, nodeTypes: nodes.map((n) => n.type), connectionCount: connections ? Object.keys(connections).length : 0 };
}

function summarizeValidation(data: Record<string, unknown>): Record<string, unknown> {
  const invalidNodes = data.invalidNodes as Array<Record<string, unknown>> | undefined;
  const orphanNodes = data.orphanNodes as Array<Record<string, unknown>> | undefined;
  return { valid: data.valid, ...(invalidNodes ? { invalidNodes: invalidNodes.map((n) => ({ type: n.type, nodeName: n.nodeName || n.name, suggestion: n.suggestion })) } : {}), ...(orphanNodes ? { orphanNodes: orphanNodes.map((n) => ({ nodeName: n.nodeName || n.name })) } : {}) };
}

function summarizePlanCreated(data: Record<string, unknown>): Record<string, unknown> {
  const plan = data.plan as Record<string, unknown> | undefined;
  if (!plan) return data;
  const steps = plan.steps as Array<Record<string, unknown>> | undefined;
  return { plan: { title: plan.title, summary: truncate(plan.summary, 200), steps: steps ? steps.map((s) => ({ ...s, description: truncate(s.description, 100), details: truncate(s.details, 100) })) : undefined } };
}

function filterSseEvent(entry: DebugLogEntry): DebugLogEntry | null {
  const event = entry.data?.event as Record<string, unknown> | undefined;
  const eventType = event?.type as string | undefined;
  if (!eventType) return null;
  const keepTypes = new Set(['error', 'complete', 'plan_created', 'questions_asked', 'insufficient_credits']);
  if (!keepTypes.has(eventType)) return null;
  if (eventType === 'complete' && event) {
    const eventData = event.data as Record<string, unknown> | undefined;
    if (eventData) {
      const { workflow, debugLog, ...rest } = eventData;
      return { ...entry, data: { event: { type: 'complete', data: { ...rest, ...(workflow ? { workflow: '[STRIPPED]' } : {}), ...(debugLog ? { debugLog: '[STRIPPED]' } : {}) } } } };
    }
  }
  return entry;
}

function summarizeAgentEvent(entry: DebugLogEntry): DebugLogEntry | null {
  const eventType = entry.data?.eventType as string | undefined;
  const redundantTypes = new Set(['tool_use', 'tool_result', 'tool_start', 'tool_end']);
  if (eventType && redundantTypes.has(eventType)) return null;
  if (eventType === 'thinking' || eventType === 'text') return { ...entry, data: { ...entry.data, content: truncate(entry.data.content, 200), text: entry.data.text ? truncate(entry.data.text, 200) : undefined } };
  return entry;
}

function compactEntry(entry: DebugLogEntry): DebugLogEntry | null {
  switch (entry.type) {
    case 'conversation_context': return { ...entry, data: summarizeConversationContext(entry.data) };
    case 'api_request': return { ...entry, data: summarizeApiRequest(entry.data) };
    case 'api_response': return { ...entry, data: summarizeApiResponse(entry.data) };
    case 'tool_result':
    case 'agent_tool_end': return { ...entry, data: summarizeToolResult(entry.data) };
    case 'workflow_generated': return { ...entry, data: summarizeWorkflowGenerated(entry.data) };
    case 'validation': return { ...entry, data: summarizeValidation(entry.data) };
    case 'plan_created': return { ...entry, data: summarizePlanCreated(entry.data) };
    case 'sse_event': return filterSseEvent(entry);
    case 'agent_event': return summarizeAgentEvent(entry);
    case 'container_raw_output': return entry;
    default: return entry;
  }
}

function collapseContainerOutput(entries: DebugLogEntry[]): DebugLogEntry[] {
  const rawEntries: DebugLogEntry[] = [];
  const result: DebugLogEntry[] = [];
  let firstTimestamp: string | undefined;
  for (const entry of entries) {
    if (entry.type === 'container_raw_output') {
      if (!firstTimestamp) firstTimestamp = entry.timestamp;
      rawEntries.push(entry);
    } else {
      result.push(entry);
    }
  }
  if (rawEntries.length === 0) return entries;
  const stdoutLines = rawEntries.filter((e) => e.data.stream === 'stdout').length;
  const stderrLines = rawEntries.filter((e) => e.data.stream === 'stderr').length;
  const errors = rawEntries.filter((e) => e.data.stream === 'stderr' || (typeof e.data.line === 'string' && /error/i.test(e.data.line))).map((e) => truncate(e.data.line, 150));
  const firstRawIndex = entries.findIndex((e) => e.type === 'container_raw_output');
  result.splice(firstRawIndex, 0, { timestamp: firstTimestamp!, type: 'container_raw_output', data: { summary: true, totalLines: rawEntries.length, stdoutLines, stderrLines, ...(errors.length > 0 ? { errors: errors.slice(0, 5) } : {}) } });
  return result;
}

function deduplicateAgentEvents(entries: DebugLogEntry[]): DebugLogEntry[] {
  const result: DebugLogEntry[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]!;
    if (entry.type !== 'agent_event') { result.push(entry); continue; }
    const eventType = entry.data?.eventType;
    let count = 1;
    while (i + 1 < entries.length) {
      const next = entries[i + 1]!;
      if (next.type !== 'agent_event' || next.data?.eventType !== eventType) break;
      count++;
      i++;
    }
    if (count > 1) result.push({ timestamp: entry.timestamp, type: entry.type, data: { ...entry.data, mergedCount: count } });
    else result.push(entry);
  }
  return result;
}

function createCompactLog(logs: DebugLog[]): DebugLog[] {
  return logs.map((log) => {
    let entries = log.entries.map(compactEntry).filter(Boolean) as DebugLogEntry[];
    entries = collapseContainerOutput(entries);
    entries = deduplicateAgentEvents(entries);
    return { ...log, entries };
  });
}

// ── Component ────────────────────────────────────────────────────────

export function ReportIssuePanel({ logs }: ReportIssuePanelProps) {
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { conversationId, currentPlan, currentWorkflow } = useChatStore();

  const submit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const apiClient = createApiClient(API_BASE_URL);
      const compact = createCompactLog(logs);
      const result = await apiClient.submitBugReport({
        description: description.trim(),
        conversationId: conversationId || undefined,
        planJson: currentPlan ? JSON.stringify(currentPlan) : undefined,
        workflowJson: currentWorkflow ? JSON.stringify(currentWorkflow) : undefined,
        compactDebugLogs: JSON.stringify(compact),
        fullDebugLogs: JSON.stringify(logs),
      });
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(true);
        setDescription('');
        setTimeout(() => setSuccess(false), 4000);
      }
    } catch {
      setError('Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col p-6">
      <div className="flex flex-col items-center mb-6">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-red-50">
          <FlagIcon className="h-6 w-6 text-red-500" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900">Report an Issue</h3>
        <p className="mt-1 text-xs text-gray-500 text-center">
          Help us improve &mdash; describe what went wrong
        </p>
      </div>

      {success ? (
        <div className="flex items-center justify-center gap-2 rounded-lg bg-green-50 p-4 text-sm text-green-700">
          <CheckIcon className="h-4 w-4" />
          Report submitted &mdash; thank you!
        </div>
      ) : (
        <div className="space-y-3">
          <textarea
            ref={textareaRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened? What did you expect?"
            rows={5}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          {logs.length > 0 && (
            <p className="text-xs text-gray-400">
              Debug logs will be attached automatically.
            </p>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !description.trim()}
            className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <SpinnerIcon className="h-4 w-4" />
                Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
